import { useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BalanceSheet() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0,10));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/reports/balance-sheet?as_of=${asOf}`);
      setData(data);
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, bold, danger }) => (
    <tr className="border-b border-forest-100">
      <td className={`py-2 ${bold ? "font-medium" : "text-forest-400"}`}>{label}</td>
      <td className={`py-2 text-right num ${danger ? "text-[#C45A3D]" : ""} ${bold ? "font-display" : ""}`}>{formatIDR(value)}</td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tag">Laporan</div>
        <h1 className="font-display text-4xl tracking-tight mt-1">Neraca Sederhana</h1>
        <p className="text-sm text-forest-400 mt-2">Posisi keuangan per tanggal — aset, kewajiban, dan modal.</p>
      </div>

      <div className="card-grid p-4 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Per Tanggal</Label><Input data-testid="inp-as-of" type="date" value={asOf} onChange={e=>setAsOf(e.target.value)} className="rounded-sm"/></div>
        <Button data-testid="btn-run-balance" onClick={run} disabled={busy} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">{busy ? "Menghitung…" : "Tampilkan"}</Button>
        {data && <Button variant="outline" onClick={()=>window.print()} className="rounded-sm gap-2"><FileDown size={16}/> Cetak PDF</Button>}
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:grid-cols-3">
          <div className="card-grid p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tag">Aset</div>
              <span className="text-xs text-forest-400">per {data.as_of}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                <Row label="Kas & Bank" value={data.aset.kas_dan_bank}/>
                <Row label="Piutang Usaha" value={data.aset.piutang_usaha}/>
                <Row label="Persediaan" value={data.aset.persediaan}/>
                <Row label="Aset Tetap" value={data.aset.aset_tetap}/>
                <tr className="border-t-2 border-[#151A18]"><td className="py-3 font-display text-lg">Total Aset</td><td data-testid="total-aset" className="py-3 text-right font-display text-lg num">{formatIDR(data.aset.total)}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="card-grid p-6">
            <div className="label-tag mb-3">Kewajiban</div>
            <table className="w-full text-sm">
              <tbody>
                <Row label="Utang Usaha" value={data.kewajiban.utang_usaha} danger/>
                <tr className="border-t-2 border-[#151A18]"><td className="py-3 font-display text-lg">Total Kewajiban</td><td data-testid="total-kewajiban" className="py-3 text-right font-display text-lg num">{formatIDR(data.kewajiban.total)}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="card-grid p-6">
            <div className="label-tag mb-3">Modal</div>
            <table className="w-full text-sm">
              <tbody>
                <Row label="Aset – Kewajiban" value={data.modal.total} bold/>
                <tr className="border-t-2 border-[#151A18]"><td className="py-3 font-display text-lg">Total Modal</td><td data-testid="total-modal" className="py-3 text-right font-display text-lg num">{formatIDR(data.modal.total)}</td></tr>
              </tbody>
            </table>
            <div className={`mt-4 p-3 rounded-sm text-sm flex items-center gap-2 ${data.balanced ? "bg-[#E9F0EB] text-[#2D4739]" : "bg-[#FBE8DF] text-[#C45A3D]"}`}>
              {data.balanced ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
              {data.balanced ? "Neraca seimbang (Aset = Kewajiban + Modal)" : "Neraca tidak seimbang, cek data invoice/pembayaran"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
